import React, { useEffect } from 'react';
import { Box, Grid, Text, Paper, Stack, Group } from '@mantine/core';
import { useQuery } from '@apollo/client';
import { GET_CUSTOMER_PROFILE } from '../../queries';

const InfoItem = ({ label, value }) => (
  <Group justify="space-between" gap="xs" wrap="nowrap">
    <Text size="sm" c="dimmed" fw={500} style={{ minWidth: 'fit-content' }}>
      {label}:
    </Text>
    <Text size="sm" fw={600} ta="right" style={{ wordBreak: 'break-word' }}>
      {value}
    </Text>
  </Group>
);

const Section = ({ title, children }) => (
  <Paper p="xs" withBorder h="100%">
    <Stack gap="xs" h="100%">
      <Text fw={700} size="md" c="dark">
        {title}
      </Text>
      <Box style={{ flex: 1 }}>
        {children}
      </Box>
    </Stack>
  </Paper>
);

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return 'N/A';
  if (amount === 0) return '$0';
  const absAmount = Math.abs(amount).toLocaleString();
  return amount < 0 ? `-$${absAmount}` : `$${absAmount}`;
};

const CustomerProfile = ({ selectedCustomer }) => {
  const { data, loading, error } = useQuery(GET_CUSTOMER_PROFILE, {
    variables: { acctNo: selectedCustomer?.acctNo || '' },
    skip: !selectedCustomer,
  });

  // Handle errors (optional: you can customize error handling further)
  useEffect(() => {
    if (error) {
      console.error('Error fetching customer profile:', error);
    }
  }, [error]);

  if (loading) return <Text p="xs">Loading...</Text>;
  if (error) return <Text p="xs" c="red">Error: {error.message}</Text>;
  if (!data) return <Text p="xs">No data available</Text>;

  const profile = data.customerProfileByAccountNumber;

  return (
    <Box p="xs">
      <Grid gutter="xs">
        {/* Top row */}
        <Grid.Col span={{ base: 12, lg: 6 }}>
          {/* Status Section */}
          <Section title="Status">
            <Grid gutter="xs">
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Stack gap={2}>
                  <InfoItem label="Key Customer" value={profile.keyCustomerYN} />
                  <InfoItem label="Customer Priority" value={profile.customerPriority} />
                  <InfoItem label="Customer Health" value={profile.customerHealth} />
                  <InfoItem label="Engine Customer" value={profile.engineCustomerYN} />
                  <InfoItem label="BD CRMS" value={profile.bdCrms} />
                  <InfoItem label="Account Approved" value={profile.accountApprovedYN} />
                  <InfoItem label="Account Type" value={profile.accountType} />
                </Stack>
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Stack gap={2}>
                  <InfoItem label="Region" value={profile.region} />
                  <InfoItem label="Country" value={profile.country} />
                  <InfoItem label="State" value={profile.state} />
                  <InfoItem label="Priority" value={profile.priority} />
                  <InfoItem label="Account Rating" value={profile.accountRating} />
                  <InfoItem label="On Hold" value={profile.onHold} />
                </Stack>
              </Grid.Col>
            </Grid>
          </Section>
        </Grid.Col>

        <Grid.Col span={{ base: 12, lg: 6 }}>
          <Grid gutter="xs">
            {/* Financials Section */}
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <Section title="Financials">
                <Stack gap={2}>
                  <InfoItem label="Credit Terms" value={profile.creditTerms} />
                  <InfoItem label="Credit Limit" value={formatCurrency(profile.creditLimit)} />
                  <InfoItem label="Open Orders" value={formatCurrency(profile.openBalance)} />
                  <InfoItem label="Credit Remaining" value={formatCurrency(profile.creditRemaining)} />
                  <InfoItem label="Annual Financial Plan" value={formatCurrency(profile.annualFinancialPlan)} />
                  <InfoItem label="YTD Invoiced" value={formatCurrency(profile.ytdInvoiced)} />
                  <InfoItem label="YTD Sales Orders" value={formatCurrency(profile.ytdSalesOrders)} />
                  <InfoItem label="Current Backlog" value={formatCurrency(profile.currentBacklog)} />
                </Stack>
              </Section>
            </Grid.Col>

            {/* Fleet Section */}
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <Section title="Fleet">
                <Stack gap={2}>
                  <InfoItem label="AVL" value={profile.avl_YN} />
                  <InfoItem label="PBH" value={profile.pbh_YN} />
                  <InfoItem label="PBH Company" value={profile.pbhCompany} />
                  <InfoItem label="Fleet Size" value={profile.fleetSize || 'N/A'} />
                  <Box mt="xs">
                    <Text size="sm" fw={600} c="dark" mb={2}>
                      Airframes
                    </Text>
                    <Text size="sm" c="dimmed" style={{ whiteSpace: 'pre-wrap' }}>
                      {profile.airframeList || 'N/A'}
                    </Text>
                  </Box>
                </Stack>
              </Section>
            </Grid.Col>
          </Grid>
        </Grid.Col>

        {/* Bottom row - Memo/Alert Notes Section */}
        <Grid.Col span={12}>
          <Section title="Memo/Alert Notes">
            <Stack gap="xs">
              <Text size="sm" fw={600} c="dark">
                Alert: {profile.alertYN}
              </Text>
              <Grid gutter="xs">
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <Paper p="xs" withBorder bg="red.0">
                    <Stack gap={2}>
                      <Text size="sm" fw={600} c="red.7">
                        Alert
                      </Text>
                      <Text size="sm" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {profile.alertText || 'No alerts'}
                      </Text>
                    </Stack>
                  </Paper>
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <Paper p="xs" withBorder bg="blue.0">
                    <Stack gap={2}>
                      <Text size="sm" fw={600} c="blue.7">
                        Memo
                      </Text>
                      <Text size="sm" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {profile.memoText || 'No memo'}
                      </Text>
                    </Stack>
                  </Paper>
                </Grid.Col>
              </Grid>
            </Stack>
          </Section>
        </Grid.Col>
      </Grid>
    </Box>
  );
};

export default CustomerProfile;